import { StandardEvents } from '@shopify/events';

/**
 * HCV Product Media Gallery
 * Custom element that manages the high-conversion gallery:
 * - Thumbnail click → setActiveMedia
 * - Product select event → setActiveMedia (no re-render)
 * - Mobile scroll → sync thumbnail state
 * - Desktop hover → update zoom origin CSS variables
 * - Preload adjacent media on activation
 */
class HcvMediaGallery extends HTMLElement {
  /** @type {string|null} */ #activeMediaId = null;
  /** @type {AbortController} */ #controller;
  /** @type {string} */ #baseRatio = '1';
  #isScrollingProgrammatically = false;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    // Wire up thumbnail clicks
    this.querySelectorAll('.hcv-media-gallery__thumb').forEach((btn) => {
      btn.addEventListener(
        'click',
        () => {
          if (!this.#selectVariantForMedia(btn.dataset.mediaId)) {
            this.setActiveMedia(btn.dataset.mediaId);
          }
        },
        { signal }
      );
    });

    // Wire up dot clicks (mobile)
    this.querySelectorAll('.hcv-media-gallery__dot').forEach((dot) => {
      dot.addEventListener(
        'click',
        () => {
          const slide = this.querySelector(
            `.hcv-media-gallery__slide[data-index="${dot.dataset.index}"]`
          );
          if (!slide) return;
          if (!this.#selectVariantForMedia(slide.dataset.mediaId)) {
            this.setActiveMedia(slide.dataset.mediaId);
          }
        },
        { signal }
      );
    });

    // Sync active state when mobile track is scrolled by touch/swipe
    const track = this.querySelector('.hcv-media-gallery__track');
    if (track) {
      track.addEventListener('scroll', this.#handleMobileScroll, { signal, passive: true });
    }

    // Desktop zoom: track mouse position to update transform-origin
    const main = this.querySelector('.hcv-media-gallery__main');
    if (main) {
      main.addEventListener('mousemove', this.#handleZoomMove, { signal, passive: true });
    }

    // Listen for Horizon's product select event on the parent section
    const section = this.closest('.shopify-section, dialog');
    if (section) {
      section.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    }

    // Capture the initial aspect ratio so we can restore it when leaving a video
    this.#baseRatio = this.style.getPropertyValue('--hcv-main-ratio') || '1';

    // Activate the first visible media item
    const firstItem = this.querySelector(
      '.hcv-media-gallery__main-item:not([data-variant-hidden="true"])'
    );
    if (firstItem) {
      this.setActiveMedia(firstItem.dataset.mediaId, false);
    }
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  /**
   * Make a media item active across desktop main area, thumbnails, and mobile dots.
   * @param {string} mediaId
   * @param {boolean} [scrollMobile=true] - Whether to scroll the mobile track to this item.
   */
  setActiveMedia(mediaId, scrollMobile = true) {
    if (mediaId == null) return;
    const mediaIdStr = String(mediaId);
    if (mediaIdStr === this.#activeMediaId) return;

    // Pause any playing media that's becoming inactive
    const currentActive = this.querySelector(
      `.hcv-media-gallery__main-item[data-media-id="${this.#activeMediaId}"]`
    );
    if (currentActive) this.#pauseMedia(currentActive);

    // Toggle active class, aria-hidden, and inert on all desktop main items
    this.querySelectorAll('.hcv-media-gallery__main-item').forEach((item) => {
      const active = item.dataset.mediaId === mediaIdStr;
      item.classList.toggle('is-active', active);
      item.setAttribute('aria-hidden', String(!active));
      item.toggleAttribute('inert', !active);
      // A variant's own media can be marked data-variant-hidden (deduped from the
      // thumbnail rail) — but it must still show when it's the active selection.
      if (active) item.removeAttribute('data-variant-hidden');
    });

    // Adapt container aspect ratio for video media
    const incomingItem = this.querySelector(
      `.hcv-media-gallery__main-item[data-media-id="${mediaIdStr}"]`
    );
    if (incomingItem) {
      const type = incomingItem.dataset.mediaType;
      if (type === 'video' || type === 'external_video') {
        const ratio = incomingItem.dataset.aspectRatio;
        if (ratio) this.style.setProperty('--hcv-main-ratio', ratio);
      } else {
        this.style.setProperty('--hcv-main-ratio', this.#baseRatio);
      }
    }

    this.#syncThumbnailState(mediaIdStr);

    if (scrollMobile) this.#syncMobileScroll(mediaIdStr);

    this.#activeMediaId = mediaIdStr;
    this.#preloadAdjacentMedia(mediaIdStr);
  }

  /**
   * If the given media belongs to a specific variant, select that variant in the
   * nearest variant-picker instead of just swapping the displayed image. The
   * picker's own change handling re-fetches and fires productSelect, which brings
   * setActiveMedia along for the ride (see #handleProductSelect).
   * @param {string} mediaId
   * @returns {boolean} true if a variant switch was triggered
   */
  #selectVariantForMedia(mediaId) {
    const item = this.querySelector(`[data-media-id="${mediaId}"][data-variant-id]`);
    if (!item) return false;

    const variantId = item.dataset.variantId;
    const section = this.closest('.shopify-section, dialog');
    const picker = section?.querySelector('variant-picker');
    if (!picker) return false;

    const input = picker.querySelector(`input[data-variant-id="${variantId}"]`);
    if (input) {
      if (input.checked) return false;
      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
      return true;
    }

    const option = picker.querySelector(`option[data-variant-id="${variantId}"]`);
    const select = option?.closest('select');
    if (select && select.value !== option.value) {
      select.value = option.value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
      return true;
    }

    return false;
  }

  /**
   * Update aria-selected and is-active on thumbnail buttons.
   * Scrolls the active thumbnail into view within the rail.
   * @param {string} mediaId
   */
  #syncThumbnailState(mediaId) {
    this.querySelectorAll('.hcv-media-gallery__thumb').forEach((btn) => {
      const active = btn.dataset.mediaId === String(mediaId);
      btn.setAttribute('aria-selected', String(active));
      btn.classList.toggle('is-active', active);
      if (active) btn.removeAttribute('data-variant-hidden');
    });

    const activeThumb = this.querySelector(
      `.hcv-media-gallery__thumb[data-media-id="${mediaId}"]`
    );
    activeThumb?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }

  /**
   * Scroll the mobile track to the slide matching mediaId.
   * @param {string} mediaId
   */
  #syncMobileScroll(mediaId) {
    const track = this.querySelector('.hcv-media-gallery__track');
    if (!track) return;
    const slide = this.querySelector(
      `.hcv-media-gallery__slide[data-media-id="${mediaId}"]`
    );
    if (!slide) return;
    const index = parseInt(slide.dataset.index, 10);
    track.scrollTo({ left: track.offsetWidth * index, behavior: 'smooth' });
    this.#isScrollingProgrammatically = true;
    track.addEventListener('scrollend', () => {
      this.#isScrollingProgrammatically = false;
    }, { once: true, signal: this.#controller.signal });
  }

  /**
   * When the mobile track is scrolled, sync active state from scroll position.
   */
  #handleMobileScroll = () => {
    if (this.#isScrollingProgrammatically) return;
    const track = this.querySelector('.hcv-media-gallery__track');
    if (!track) return;

    const index = Math.round(track.scrollLeft / track.offsetWidth);
    const slides = this.querySelectorAll('.hcv-media-gallery__slide');
    const activeSlide = slides[index];
    if (!activeSlide || activeSlide.dataset.mediaId === this.#activeMediaId) return;

    this.setActiveMedia(activeSlide.dataset.mediaId, false);

    // Sync dots
    this.querySelectorAll('.hcv-media-gallery__dot').forEach((dot, i) => {
      dot.classList.toggle('is-active', i === index);
    });
  };

  /**
   * Track mouse position within the main area to set the zoom transform-origin.
   * @param {MouseEvent} event
   */
  #handleZoomMove = (event) => {
    const activeItem = this.querySelector('.hcv-media-gallery__main-item.is-active');
    if (!activeItem) return;
    const type = activeItem.dataset.mediaType;
    if (type === 'video' || type === 'external_video') return;
    const rect = activeItem.getBoundingClientRect();
    const x = (((event.clientX - rect.left) / rect.width) * 100).toFixed(1);
    const y = (((event.clientY - rect.top) / rect.height) * 100).toFixed(1);
    activeItem.style.setProperty('--zoom-x', `${x}%`);
    activeItem.style.setProperty('--zoom-y', `${y}%`);
  };

  /**
   * Handle Horizon's product select event.
   * Switches active media to the variant's featured_media if present.
   * Does NOT re-render the gallery HTML, so thumbnails stay browsable.
   * @param {ProductSelectEvent} event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail) return;

        const resource = detail.resource;

        // A variant with no image of its own should leave the gallery where it is.
        if (resource && !resource.featured_media) return;

        // The re-rendered section sorts the selected variant's media first, so it
        // stands in if the variant JSON ever arrives without a usable media id.
        const mediaId =
          resource?.featured_media?.id ??
          detail.html?.querySelector('.hcv-media-gallery__main-item')?.dataset.mediaId;

        if (mediaId != null) this.setActiveMedia(String(mediaId));
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn('[hcv-media-gallery] Event promise rejected:', error);
        }
      });
  };

  /**
   * Eagerly load the images adjacent to the current active item.
   * @param {string} mediaId
   */
  #preloadAdjacentMedia(mediaId) {
    const items = [
      ...this.querySelectorAll(
        '.hcv-media-gallery__main-item:not([data-variant-hidden="true"])'
      ),
    ];
    const index = items.findIndex((el) => el.dataset.mediaId === String(mediaId));

    [-1, 1].forEach((offset) => {
      const adjacent = items[index + offset];
      if (!adjacent) return;
      const adjacentId = adjacent.dataset.mediaId;

      // Preload in desktop main area
      adjacent.querySelectorAll('img[loading="lazy"]').forEach((img) => {
        img.loading = 'eager';
      });

      // Preload matching mobile slide
      const mobileSlide = this.querySelector(
        `.hcv-media-gallery__slide[data-media-id="${adjacentId}"]`
      );
      mobileSlide?.querySelectorAll('img[loading="lazy"]').forEach((img) => {
        img.loading = 'eager';
      });
    });
  }

  /**
   * Pause any playing video or iframe inside the given element.
   * @param {HTMLElement} container
   */
  #pauseMedia(container) {
    container.querySelectorAll('video').forEach((video) => {
      if (!video.paused) video.pause();
    });
    // For external videos (YouTube/Vimeo iframes), reset src to stop playback
    container.querySelectorAll('iframe').forEach((iframe) => {
      const src = iframe.src;
      if (src) {
        iframe.src = '';
        iframe.src = src;
      }
    });
  }
}

if (!customElements.get('hcv-media-gallery')) {
  customElements.define('hcv-media-gallery', HcvMediaGallery);
}
